import React, { useEffect, useMemo, useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Divider,
  List,
  useMediaQuery,
  Collapse,
  Avatar,
  Typography,
} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import MenuIcon from "@mui/icons-material/Menu";
import MuiAppBar from "@mui/material/AppBar";
import CssBaseline from "@mui/material/CssBaseline";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import LogoutIcon from "@mui/icons-material/Logout";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  globalStyle,
  handleDangerVisible,
  handleVisible,
} from "../../global/Utils";
import { menuItems as DrawerListItems, isMenuItemActive } from "./MenuItem";
import {
  DI_APP_MODE,
  appMode as appModeOptions,
  normalizeAppModes,
} from "../Users/Variables";
import ChangePasswordModal from "./ChangePasswordModal";
import { ErrorBoundary } from "../Common/ErrorBoundary";
import PasswordIcon from "@mui/icons-material/Password";
import { getUserDetails } from "../../Actions/HeaderAction";
import { useDispatch, useSelector } from "react-redux";
import { changePassword } from "../../Actions/HeaderAction";
import ebeaconsoftsLogo from "../../Images/ebeaconsoftsLogo.png";

const drawerWidth = 240;
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        [theme.breakpoints.up("md")]: {
          marginLeft: drawerWidth,
          width: `calc(100% - ${drawerWidth}px)`,
        },
        transition: theme.transitions.create(["width", "margin"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open
    ? {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": {
          ...openedMixin(theme),
          backgroundColor: globalStyle.bgColorWhite,
        },
      }
    : {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": {
          ...closedMixin(theme),
          backgroundColor: globalStyle.bgColorWhite,
        },
      }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AdminHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // const [open, setOpen] = useState(true);
  const open = true;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openModalPassword, setOpenPasswordModal] = useState(false);
  const { userDetails, isChangingPassword } = useSelector(
    (state) => state?.HeaderReducer,
  );

  const handleMobileDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.replace("/login");
  };

  const handleNavigate = (path) => {
    if (isMobile) {
      navigate(path);
      handleMobileDrawerToggle();
    } else {
      navigate(path);
    }
  };

  // Change Password
  const handleOpenPasswordModal = () => {
    setOpenPasswordModal(true);
    handleMenuClose();
  };

  // Modes that decide which mode-specific tabs this user sees. Admins get all of
  // them; a user saved before App Mode was multi-select (or with none set) keeps
  // the DI screens so nothing disappears for them.
  const userModes = useMemo(() => {
    if (userDetails?.role === "admin")
      return appModeOptions.map((item) => item.value);
    const modes = normalizeAppModes(userDetails?.appMode);
    return modes.length ? modes : [DI_APP_MODE];
  }, [userDetails]);

  const isVisibleForModes = (modes) =>
    !modes?.length || modes.some((mode) => userModes.includes(mode));

  // Get User Role
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(getUserDetails());
    }, 500);
    return () => clearTimeout(timer);
  }, [dispatch]);

  const drawerContent = (
    <>
      <DrawerHeader
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          py: 2,
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#F0F2F7",
        }}
      >
        <Box
          component="img"
          onClick={() =>
            navigate(
              userDetails?.role !== "admin" ? "/main" : "/main/admin-dashboard",
            )
          }
          src={ebeaconsoftsLogo}
          alt="logo"
          sx={{
            width: "90%",
            height: isMobile ? 40 : 65,
            objectFit: "contain",
            cursor: "pointer",
          }}
        />
      </DrawerHeader>
      <Divider />
      <List>
        {DrawerListItems.map((item) => {
          if (
            userDetails?.role !== "admin" &&
            (item.key === "users" || item.key === "admin-dashboard" || item.key === "subscriptions" || item.key === "program-settings")
          )
            return null;
          else if (userDetails?.role === "admin" && item.key === "dashboard")
            return null;
          else if (!isVisibleForModes(item.modes)) return null;

          const visibleSubItems = item.subItems?.filter((sub) =>
            isVisibleForModes(sub.modes),
          );
          if (item.subItems && !visibleSubItems.length) return null;
          const isActive = isMenuItemActive(
            item,
            visibleSubItems,
            location.pathname,
          );

          return (
            <React.Fragment key={item.key}>
              {/* Main ListItem */}
              <ListItem disablePadding sx={{ backgroundColor: "#e6eaef" }}>
                <ListItemButton
                  sx={{ justifyContent: open ? "initial" : "center" }}
                  onClick={() => {
                    if (!item.subItems) {
                      handleNavigate(item.path);
                    }
                  }}
                >
                  <ListItemIcon sx={{ justifyContent: "center" }}>
                    {React.cloneElement(item.icon, {
                      sx: {
                        color: isActive ? globalStyle.blueColor : "",
                        transition: "color 0.3s ease",
                      },
                    })}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.title}
                    sx={{
                      opacity: isMobile ? 1 : open ? 1 : 0, // show on mobile
                      color: isActive ? globalStyle.blueColor : "",
                    }}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontFamily: globalStyle.fontFamilyParagraph,
                      fontWeight: "bold",
                    }}
                  />
                </ListItemButton>
              </ListItem>

              {/* Collapse if subItems exist */}
              {item.subItems && (
                <Collapse
                  in={isMobile ? mobileOpen : open}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {visibleSubItems.map((sub, index) => (
                      <ListItem key={index} disablePadding>
                        <ListItemButton
                          onClick={() => handleNavigate(sub.path)}
                          sx={{
                            pl: open ? 3 : 2,
                            justifyContent: open ? "initial" : "center",
                            mt: -1,
                            color:
                              location.pathname === sub.path
                                ? globalStyle.blueColor
                                : "",
                            "&:hover": {
                              color: globalStyle.blueColor,
                              backgroundColor: "transparent",
                            },
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              right: 0,
                              top: 8,
                              bottom: 8,
                              width: "5px",
                              borderRadius: "1px 0 0 1px",
                              backgroundColor:
                                location.pathname === sub.path
                                  ? globalStyle.blueColor
                                  : "transparent",
                              transition: "all 0.3s ease",
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{ justifyContent: "center", color: "inherit" }}
                          >
                            {/* Optional sub-item icon */}
                          </ListItemIcon>
                          <ListItemText
                            primary={sub.title}
                            sx={{ opacity: isMobile ? 1 : open ? 1 : 0 }}
                            primaryTypographyProps={{
                              fontSize: 14,
                              fontFamily: globalStyle.fontFamilyParagraph,
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>
    </>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        open={open}
        sx={{ backgroundColor: globalStyle.bgColorWhite }}
      >
        <Toolbar>
          {/* Mobile Menu Icon */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleMobileDrawerToggle}
              sx={{ mr: 2, color: "black" }}
            >
              <MenuIcon />
            </IconButton>
          )}
          {/* <Button
            onClick={() =>
              navigate(
                userDetails?.role !== "admin"
                  ? "/main"
                  : "/main/admin-dashboard",
              )
            }
            sx={{
              mb: 1,
              mt: 1,
              width: isMobile ? "auto" : 250,
              height: 57,
              borderRadius: 0,
              fontSize: isMobile ? "0.75rem" : "1rem",
              padding: isMobile ? "0 8px" : "0 16px",
            }}
          >
            {isMobile ? "Ebeaconsofts Digital Invoicing" : "Ebeaconsofts Digital Invoicing"}
          </Button> */}
          <Box flexGrow={1} />
          <Box display="flex" alignSelf="center" ml={1}>
            <IconButton edge="end" onClick={handleProfileMenuOpen}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: "#1976d2",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                }}
              >
                {userDetails?.userName
                  .split(" ")
                  .map((word) => word[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2)}
              </Avatar>
            </IconButton>
          </Box>

          {/* Profile Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              elevation: 4,
              sx: {
                mt: 1.5,
                borderRadius: 3,
                minWidth: 280,
                p: 2,
                overflow: "visible",
                boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            {/* Top Profile Section */}
            <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: "#1976d2",
                  fontSize: "1rem",
                  fontWeight: 600,
                }}
              >
                {userDetails?.userName
                  ?.split(" ")
                  ?.map((word) => word[0])
                  ?.join("")
                  ?.toUpperCase()
                  ?.slice(0, 2)}
              </Avatar>

              <Box>
                <Typography fontWeight={600} fontSize="0.95rem">
                  {userDetails?.userName}
                </Typography>
                <Typography fontSize="0.8rem" color="text.secondary">
                  {userDetails?.email}
                </Typography>
                <Typography fontSize="0.75rem" color="primary">
                  {userDetails?.role}
                </Typography>
              </Box>
            </Box>

            {/* Credits Section */}
            {userDetails?.role !== "admin" && (
              <Box
                sx={{
                  background: "#f5f8ff",
                  borderRadius: 2,
                  p: 1.2,
                  mb: 1.5,
                }}
              >
                {/* Invoice Credits */}
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography fontSize="0.8rem">Invoice Credits</Typography>
                  <Typography fontSize="0.8rem" fontWeight={600}>
                    {userDetails?.invoiceCount}/{userDetails?.allowInvoices}
                  </Typography>
                </Box>

                {/* Progress Bar */}
                <Box
                  sx={{
                    height: 6,
                    borderRadius: 5,
                    background: "#e0e0e0",
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: `${
                        ((userDetails?.invoiceCount > userDetails?.allowInvoices
                          ? userDetails?.allowInvoices
                          : userDetails?.invoiceCount) /
                          userDetails?.allowInvoices) *
                          100 || 0
                      }%`,
                      height: "100%",
                      borderRadius: 5,
                      background: "#1976d2",
                    }}
                  />
                </Box>

                {/* Company Credits */}
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography fontSize="0.8rem">Company Credits</Typography>
                  <Typography fontSize="0.8rem" fontWeight={600}>
                    {userDetails?.companyCount > userDetails?.allowCompanies
                      ? userDetails?.allowCompanies
                      : userDetails?.companyCount}
                    /{userDetails?.allowCompanies}
                  </Typography>
                </Box>

                {/* Progress Bar */}
                <Box
                  sx={{
                    height: 6,
                    borderRadius: 5,
                    background: "#e0e0e0",
                  }}
                >
                  <Box
                    sx={{
                      width: `${
                        (userDetails?.companyCount /
                          userDetails?.allowCompanies) *
                          100 || 0
                      }%`,
                      height: "100%",
                      borderRadius: 5,
                      background: "#4caf50",
                    }}
                  />
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 1 }} />

            {/* Actions */}
            <MenuItem
              onClick={handleOpenPasswordModal}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              <ListItemIcon>
                <PasswordIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Change Password" />
            </MenuItem>

            <MenuItem
              onClick={handleLogout}
              sx={{
                borderRadius: 2,
                "&:hover": { backgroundColor: "#fdecea" },
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" sx={{ color: "red" }} />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{ color: "red" }}
              />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer (temporary) */}
      {isMobile ? (
        <MuiDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleMobileDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              backgroundColor: globalStyle.bgColorWhite,
              boxSizing: "border-box",
            },
          }}
        >
          {drawerContent}
        </MuiDrawer>
      ) : (
        /* Desktop Drawer (permanent with hover) */
        <Drawer
          variant="permanent"
          open={open}
          // onMouseOver={handleDrawerOpen}
          // onMouseLeave={handleDrawerClose}
          sx={{
            display: { xs: "none", md: "block" },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <DrawerHeader />
        {/* A page that fails shows its error here; the menu stays usable,
            and opening another page tries again. */}
        <ErrorBoundary resetKey={location.pathname}>
          <Outlet />
        </ErrorBoundary>
        <Box
          component="footer"
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            zIndex: 1000,
            textAlign: "center",
            borderTop: "1px solid #e0e0e0",
            backgroundColor: "#f8fafc",
            py: 0.8,
            fontSize: "12px",
          }}
        >
          © 2026{" "}
          <a
            href="https://ebeaconsofts.com/"
            rel="noopener noreferrer"
            target="_blank"
            style={{
              fontWeight: 600,
              color: "#1976d2", // MUI blue
              textDecoration: "none",
            }}
          >
            EBEACONSOFTS SMC PVT LTD
          </a>{" "}
          . All Rights Reserved.
        </Box>
      </Box>
      <ChangePasswordModal
        loader={isChangingPassword}
        openModalPassword={openModalPassword}
        setOpenPasswordModal={setOpenPasswordModal}
        isFromMui={true}
        handleSubmit={(value, closeModal) => {
          dispatch(
            changePassword(
              value,
              handleVisible,
              handleDangerVisible,
              closeModal,
            ),
          );
        }}
      />
    </Box>
  );
};

export default AdminHeader;
